import React, { useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import api from '../../services/api';

const WizardContainer = styled.div`
  max-width: 800px;
  margin: 40px auto;
  padding: 30px;
  background: ${({ theme }) => theme?.mode === 'dark' ? '#1f2937' : 'white'};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme?.mode === 'dark' ? 'none' : '0 4px 20px rgba(0,0,0,0.08)'};
  border: 1px solid ${({ theme }) => theme?.mode === 'dark' ? '#374151' : 'transparent'};
`;

const WizardHeader = styled.div`
  margin-bottom: 30px;
  text-align: center;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme?.mode === 'dark' ? '#f9fafb' : '#2d3748'};
  margin-bottom: 10px;
`;

const Steps = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 40px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 15px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : '#e2e8f0'};
    z-index: 0;
  }
`;

const Step = styled.div`
  position: relative;
  z-index: 1;
  background: ${({ theme }) => theme?.mode === 'dark' ? '#1f2937' : 'white'};
  padding: 0 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const StepCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.active ? '#667eea' : props.completed ? '#48bb78' : props.theme?.mode === 'dark' ? '#374151' : '#e2e8f0'};
  color: ${props => props.active || props.completed ? 'white' : props.theme?.mode === 'dark' ? '#9ca3af' : '#718096'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  transition: all 0.3s;
`;

const StepLabel = styled.span`
  font-size: 12px;
  color: ${props => props.active ? '#667eea' : props.theme?.mode === 'dark' ? '#9ca3af' : '#718096'};
  font-weight: ${props => props.active ? '600' : '400'};
`;

const FormSection = styled.div`
  margin-bottom: 30px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: ${({ theme }) => theme?.mode === 'dark' ? '#e5e7eb' : '#4a5568'};
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme?.mode === 'dark' ? '#4b5563' : '#e2e8f0'};
  border-radius: 6px;
  font-size: 16px;
  transition: border-color 0.2s;
  background: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : 'white'};
  color: ${({ theme }) => theme?.mode === 'dark' ? '#f9fafb' : 'inherit'};

  &:focus {
    outline: none;
    border-color: #667eea;
  }
  
  &::placeholder {
    color: ${({ theme }) => theme?.mode === 'dark' ? '#9ca3af' : '#a0aec0'};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme?.mode === 'dark' ? '#4b5563' : '#e2e8f0'};
  border-radius: 6px;
  font-size: 16px;
  background: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : 'white'};
  color: ${({ theme }) => theme?.mode === 'dark' ? '#f9fafb' : 'inherit'};
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme?.mode === 'dark' ? '#4b5563' : '#e2e8f0'};
  border-radius: 6px;
  font-size: 16px;
  min-height: 120px;
  resize: vertical;
  background: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : 'white'};
  color: ${({ theme }) => theme?.mode === 'dark' ? '#f9fafb' : 'inherit'};

  &:focus {
    outline: none;
    border-color: #667eea;
  }
  
  &::placeholder {
    color: ${({ theme }) => theme?.mode === 'dark' ? '#9ca3af' : '#a0aec0'};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 40px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;

  ${props => props.primary ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4); }
    &:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }
  ` : `
    background: ${props.theme?.mode === 'dark' ? '#374151' : '#edf2f7'};
    color: ${props.theme?.mode === 'dark' ? '#e5e7eb' : '#4a5568'};
    &:hover { background: ${props.theme?.mode === 'dark' ? '#4b5563' : '#e2e8f0'}; }
  `}
`;

const ResultCard = styled.div`
  background: ${({ theme }) => theme?.mode === 'dark' ? '#374151' : '#f7fafc'};
  border: 1px solid ${({ theme }) => theme?.mode === 'dark' ? '#4b5563' : '#e2e8f0'};
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  color: ${({ theme }) => theme?.mode === 'dark' ? '#f9fafb' : 'inherit'};
`;

const ContentWizard = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = { mode: isDark ? 'dark' : 'light' };
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [formData, setFormData] = useState({
    topic: '',
    contentType: 'blog_post',
    platform: 'blog',
    audience: '',
    tone: 'professional',
    keywords: ''
  });

  const steps = [
    { number: 1, label: 'Strategy' },
    { number: 2, label: 'Audience' },
    { number: 3, label: 'Review' },
    { number: 4, label: 'Result' }
  ];

  const handleNext = async () => {
    if (step === 3) {
      setLoading(true);
      try {
        const response = await api.post('/formal-crew/workflows', {
          workflow_type: 'content_production',
          objective: `Create ${formData.contentType} about ${formData.topic}`,
          context: {
            topic: formData.topic,
            content_type: formData.contentType,
            platform: formData.platform,
            audience: formData.audience,
            tone: formData.tone,
            keywords: formData.keywords.split(',').map(k => k.trim())
          }
        });

        if (response.data.success) {
          // In a real async scenario we might poll, but here we assume the result comes back 
          // or we display the initial success and let the user check the command center.
          // However, for the wizard experience, we want to show something.
          // If the backend runs async, we might need to wait or show a "Processing" state.
          // Based on previous analysis, run_async_ai_task awaits execution.

          const result = response.data.result;
          setGeneratedContent(result);
          setStep(4);
        }
      } catch (error) {
        console.error("Error generating content:", error);
        alert("Failed to generate content. Please try again.");
      } finally {
        setLoading(false);
      }
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <ThemeProvider theme={theme}>
      <WizardContainer>
        <WizardHeader>
          <Title>✍️ Content Studio Wizard</Title>
          <p style={{ color: isDark ? '#9ca3af' : '#718096' }}>Create high-quality, AI-optimized content in minutes</p>
        </WizardHeader>

      <Steps>
        {steps.map(s => (
          <Step key={s.number}>
            <StepCircle
              active={step === s.number}
              completed={step > s.number}
            >
              {step > s.number ? '✓' : s.number}
            </StepCircle>
            <StepLabel active={step === s.number}>{s.label}</StepLabel>
          </Step>
        ))}
      </Steps>

      <FormSection>
        {step === 1 && (
          <>
            <FormGroup>
              <Label>What is the topic?</Label>
              <Input
                name="topic"
                value={formData.topic}
                onChange={handleChange}
                placeholder="e.g., The Future of AI in Marketing"
              />
            </FormGroup>
            <FormGroup>
              <Label>Content Type</Label>
              <Select name="contentType" value={formData.contentType} onChange={handleChange}>
                <option value="blog_post">Blog Post</option>
                <option value="social_post">Social Media Post</option>
                <option value="email_campaign">Email Campaign</option>
                <option value="ad_copy">Ad Copy</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Platform</Label>
              <Select name="platform" value={formData.platform} onChange={handleChange}>
                <option value="blog">Company Blog</option>
                <option value="linkedin">LinkedIn</option>
                <option value="twitter">Twitter / X</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
              </Select>
            </FormGroup>
          </>
        )}

        {step === 2 && (
          <>
            <FormGroup>
              <Label>Target Audience</Label>
              <Input
                name="audience"
                value={formData.audience}
                onChange={handleChange}
                placeholder="e.g., Marketing Professionals, Small Business Owners"
              />
            </FormGroup>
            <FormGroup>
              <Label>Tone of Voice</Label>
              <Select name="tone" value={formData.tone} onChange={handleChange}>
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="enthusiastic">Enthusiastic</option>
                <option value="authoritative">Authoritative</option>
                <option value="witty">Witty</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Keywords (comma separated)</Label>
              <Input
                name="keywords"
                value={formData.keywords}
                onChange={handleChange}
                placeholder="e.g., AI, automation, efficiency, growth"
              />
            </FormGroup>
          </>
        )}

        {step === 3 && (
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ color: isDark ? '#f9fafb' : 'inherit' }}>Ready to Generate?</h3>
            <p style={{ color: isDark ? '#9ca3af' : '#718096', marginBottom: '20px' }}>
              Our AI agents will research, draft, and optimize your content based on these settings.
            </p>
            <div style={{ background: isDark ? '#374151' : '#f7fafc', padding: '20px', borderRadius: '8px', textAlign: 'left', display: 'inline-block', border: isDark ? '1px solid #4b5563' : 'none', color: isDark ? '#f9fafb' : 'inherit' }}>
              <p><strong>Topic:</strong> {formData.topic}</p>
              <p><strong>Type:</strong> {formData.contentType}</p>
              <p><strong>Audience:</strong> {formData.audience}</p>
              <p><strong>Tone:</strong> {formData.tone}</p>
            </div>
          </div>
        )}

        {step === 4 && generatedContent && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '48px' }}>✨</span>
              <h3 style={{ color: isDark ? '#f9fafb' : 'inherit' }}>Content Generated Successfully!</h3>
            </div>

            <ResultCard>
              <h4 style={{ color: isDark ? '#f9fafb' : 'inherit' }}>Generated Content</h4>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: '10px', color: isDark ? '#d1d5db' : 'inherit' }}>
                {generatedContent.results?.content_creation?.content ||
                  generatedContent.results?.content_creation?.result?.content ||
                  "Content generated successfully. Check the Command Center for details."}
              </div>
            </ResultCard>

            {generatedContent.results?.content_optimization && (
              <ResultCard>
                <h4 style={{ color: isDark ? '#f9fafb' : 'inherit' }}>Optimization Insights</h4>
                <div style={{ whiteSpace: 'pre-wrap', marginTop: '10px', color: isDark ? '#d1d5db' : 'inherit' }}>
                  {JSON.stringify(generatedContent.results.content_optimization, null, 2)}
                </div>
              </ResultCard>
            )}
          </div>
        )}
      </FormSection>

      <ButtonGroup>
        {step > 1 && step < 4 && (
          <Button onClick={handleBack}>Back</Button>
        )}
        {step < 3 && (
          <Button primary onClick={handleNext}>Next</Button>
        )}
        {step === 3 && (
          <Button primary onClick={handleNext} disabled={loading}>
            {loading ? 'Generating...' : 'Generate Content'}
          </Button>
        )}
        {step === 4 && (
          <Button primary onClick={() => navigate('/ai-command-center')}>
            Go to Command Center
          </Button>
        )}
      </ButtonGroup>
      </WizardContainer>
    </ThemeProvider>
  );
};

export default ContentWizard;
